<script>
    import { onMount, createEventDispatcher } from 'svelte';

    wyvr: {
        render: 'hydrate';
        loading: 'instant';
    }

    export let data;
    export let auto_select_first = true;
    export let pre_selected_options = undefined;

    const dispatch = createEventDispatcher();
    let selected = {};
    let all_selected = false;
    let debouncer;
    let started = false;

    let all_enabled_ids = [];

    $: {
        if (pre_selected_options && started) {
            set_selected(pre_selected_options);
        }
    }

    onMount(() => {
        for (const option of data) {
            selected[option.attribute_code] = false;
        }
        update_enabled_ids(selected);
        started = true;
        // when pre_selected_options is set apply it, but only at mounting
        if (pre_selected_options) {
            set_selected(pre_selected_options);
            return;
        }
        // try to auto select the first simple
        if (auto_select_first) {
            let select_ids = [];

            // options are the different attribute options for the configuration
            data.find((option) => {
                if (!Array.isArray(option.values) || option.values.length === 0 || !option.in_stock) {
                    return false;
                }
                const attribute_code = option.attribute_code;

                // search the first in_stock attribute value of the option
                const value_in_stock = option.values
                    .filter((option_value) => {
                        return option_value.in_stock;
                    })
                    .find(Boolean);

                if (value_in_stock) {
                    select_ids = [get_id(attribute_code, value_in_stock.key)];
                    if (value_in_stock.enabled_options) {
                        for (const [attribute, ids] of Object.entries(value_in_stock.enabled_options)) {
                            select_ids.push(get_id(attribute, ids.find(Boolean)));
                        }
                    }
                    return true;
                }
                return false;
            });
            // select the inputs
            select_inputs(select_ids);
        }
    });

    function set_selected(options) {
        if (options) {
            const select_ids = [];
            for (const [attribute_code, value] of Object.entries(options)) {
                select_ids.push(get_id(attribute_code, value.key));
            }
            select_inputs(select_ids);
        }
    }

    function select_inputs(select_ids) {
        if (Array.isArray(select_ids) && data.length === select_ids.length) {
            for (const id of select_ids) {
                const el = document.getElementById(id);
                if (el) {
                    el.checked = true;
                    el.dispatchEvent(new Event('change'));
                }
            }
        }
    }

    function on_change(event, option, value) {
        selected[option.attribute_code] = value;
        // debounce selected event
        clearTimeout(debouncer);
        debouncer = setTimeout(() => {
            all_selected = Object.values(selected).filter((x) => x).length == data.length;
            if (all_selected) {
                dispatch('selected', selected);
            }
        }, 100);
        update_enabled_ids(selected);
    }

    function update_enabled_ids(selected) {
        const enabled_ids = [];
        const attributes = Object.keys(selected);
        // if only one attribute is present, enable all
        if (attributes.length === 1) {
            all_enabled_ids = null;
            return;
        }
        // if nothing is selected enable all options in stock
        if (attributes.every((key) => selected[key] === false)) {
            all_enabled_ids = null;
            return;
        }
        for (const attribute of attributes) {
            if (selected[attribute].enabled_options) {
                const ids = Object.keys(selected[attribute].enabled_options).map((attribute_code) => {
                    return selected[attribute].enabled_options[attribute_code].map((key) => get_id(attribute_code, key)).filter((x) => x);
                });
                enabled_ids.push(...ids);
            }
        }

        all_enabled_ids = enabled_ids.flat(2);
    }
    function get_id(attribute_code, key) {
        return `${attribute_code}_${key}`;
    }
    function in_list(id, list) {
        return list.indexOf(id) > -1;
    }
</script>

{#if data}
    <section>
        {#each data as option}
            <div class="option">
                <h2 class="h2">{option.label}</h2>
                <div class="m-product-configurable-options m-product-detail__option-details">
                    {#each option.values as value}
                        {@const id = get_id(option.attribute_code, value.key)}
                        {@const is_disabled = !value.in_stock || (all_enabled_ids !== null && !in_list(id, all_enabled_ids))}

                        <input type="radio" name={option.attribute_code} value={value.key} disabled={is_disabled} on:change={(ev) => on_change(ev, option, value)} {id} />
                        <label for={id}>{value.title}</label>
                    {/each}
                </div>
                {#if option.attribute_code == 'kw_sizeindex'}
                    <a href="/de/informationen/koffergroessen/">Hier kommen Sie zur Größentabelle</a>
                {/if}
            </div>
        {/each}
    </section>
{/if}


<style>
    h2 {
        margin-top: 1rem;
        margin-bottom: 1rem;
    }

    section {
        margin: 0.5rem 0 1rem;
        border-top: 1px solid #707070;
    }

    section > div > div {
        margin-bottom: 16px;
    }

    a {
        color: #707070;
        font-size: 1rem;
        font-weight: 400;
        line-height: 1.8;
    }

    .option {
        margin: 0 0 1rem;
    }

    input[type='radio'] {
        clip: rect(0 0 0 0);
        clip-path: inset(100%);
        height: 1px;
        overflow: hidden;
        position: absolute;
        white-space: nowrap;
        width: 1px;
    }

    label {
        padding: 8px 10px;
        border: 1px solid var(--gray);
        font-size: var(--h4__font-size);
        font-weight: var(--h4__font-weight);
        line-height: var(--h4__line-height);
        color: var(--h4__color);
        margin: var(--h4__margin);
        text-transform: var(--font-heading-text-transform);
        font-weight: var(--font-heading-weight__base);
        color: var(--font-heading-color__base);
        margin: 0 5px 5px 0;
        cursor: pointer;
        -webkit-transition: border 0.5s ease-in-out;
        transition: border 0.5s ease-in-out;

        @media (min-width: 992px) {
            padding: 12px 20px;
            font-size: var(--h4__font-size-desktop);
        }
    }

    label:hover {
        border: 1px solid var(--darkgray);
    }

    input[type='radio']:checked + label {
        border: 1px solid var(--darkgray);
    }

    input[type='radio']:disabled + label {
        pointer-events: none;
        opacity: 0.5;
    }
</style>